import type React from "react";

import { Document, Page, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import type { UserCourseExercise } from "api/schemas/UserCourse";

import { ExerciseItem } from "./ExerciseItem";
import { pdfStyles } from "./pdfStyles";
import type { PDFPatientInfo, PDFTranslations } from "./types";

type ProtocolAsPDFProps = {
  exercises: UserCourseExercise[];
  translations: PDFTranslations;
  patientInfo: PDFPatientInfo;
  formattedDate: string;
};

export const ProtocolAsPDF: React.FC<ProtocolAsPDFProps> = ({
  exercises,
  translations,
  patientInfo,
  formattedDate,
}) => {
  const { t } = useTranslation();

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.patientInfoHeader}>
          <View style={pdfStyles.patientInfoRow}>
            <View style={pdfStyles.patientInfoCell}>
              <Text style={pdfStyles.patientInfoLabel}>{t("patients.extended_medical_note.patient_name.label")}</Text>
              <Text style={pdfStyles.patientInfoValue}>{patientInfo.name}</Text>
            </View>
            <View style={pdfStyles.patientInfoCell}>
              {patientInfo.identifier && (
                <>
                  <Text style={pdfStyles.patientInfoLabel}>{patientInfo.identifierLabel}</Text>
                  <Text style={pdfStyles.patientInfoValue}>{patientInfo.identifier}</Text>
                </>
              )}
            </View>
            <View style={pdfStyles.patientInfoCellRight}>
              <Text style={pdfStyles.patientInfoLabel}>{t("patients.protocol.print_date")}</Text>
              <Text style={pdfStyles.patientInfoValue}>{formattedDate}</Text>
            </View>
          </View>
          <View style={pdfStyles.patientInfoRow}>
            <View style={pdfStyles.patientInfoCell}>
              <Text style={pdfStyles.patientInfoLabel}>{t("patients.protocol.responsible_clinician")}</Text>
              <Text style={pdfStyles.patientInfoValue}>{patientInfo.therapistName}</Text>
            </View>
            <View style={pdfStyles.patientInfoCell}>
              <Text style={pdfStyles.patientInfoLabel}>{t("partner_patients.clinic")}</Text>
              <Text style={pdfStyles.patientInfoValue}>{patientInfo.clinicName}</Text>
            </View>
            <View style={pdfStyles.patientInfoCellRight} />
          </View>
        </View>
        <View>
          {exercises.map(exercise => (
            <ExerciseItem key={exercise.id} exercise={exercise} translations={translations} />
          ))}
        </View>

        <Text
          style={pdfStyles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};
